import { useEffect, useState, useCallback } from "react";
import { http, fmtErr, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";

const EMPTY = { image_url: "", alt_text: "", title: "", category: "Property", room_id: "", is_cover: false, sort_order: 0, active: true };
const CATS = ["Property", "Rooms", "Bedrooms", "Living", "Dining", "Outdoor", "Views", "Villa", "Other"];

const toPayload = (g) => ({
  image_url: g.image_url || "", alt_text: g.alt_text || "", title: g.title || "",
  category: g.category || "Property", room_id: g.room_id || null,
  is_cover: !!g.is_cover, sort_order: Number(g.sort_order) || 0, active: g.active !== false,
});

export default function GalleryManager() {
  const [images, setImages] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    http.get("/admin/gallery").then((r) => setImages(r.data.images)).catch(() => {});
    http.get("/admin/rooms/all").then((r) => setRooms(r.data.rooms)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.image_url) return toast.error("Upload a photo or paste an image URL first");
    setBusy(true);
    try {
      await http.post("/admin/gallery", { ...form, room_id: form.room_id || null, sort_order: Number(form.sort_order) || 0 });
      toast.success("Photo added");
      setForm(EMPTY);
      load();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  const update = async (g, patch) => {
    try {
      await http.put(`/admin/gallery/${g.id}`, { ...toPayload(g), ...patch });
      toast.success("Saved");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const remove = async (g) => {
    if (!window.confirm("Remove this photo from the website gallery?")) return;
    try {
      await http.delete(`/admin/gallery/${g.id}`);
      toast.success("Photo removed");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="space-y-6" data-testid="gallery-manager">
      <div className="card">
        <h3 className="font-serif text-2xl mb-4">Add Photo</h3>
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <ImageUpload testid="gallery-upload" label="Upload photo" onUploaded={(url) => setForm((f) => ({ ...f, image_url: url }))} />
          <span className="text-xs" style={{ color: "var(--muted)" }}>or paste an image URL:</span>
          <input data-testid="gallery-form-url" placeholder="https://…" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} style={{ flex: 1, minWidth: 220 }} />
        </div>
        {form.image_url && <img src={fileUrl(form.image_url)} alt="preview" className="h-24 rounded-lg object-cover mb-3" data-testid="gallery-form-preview" />}
        <div className="grid md:grid-cols-3 gap-3">
          <input data-testid="gallery-form-caption" placeholder="Caption" value={form.alt_text} onChange={(e) => setForm({ ...form, alt_text: e.target.value })} />
          <select data-testid="gallery-form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            {CATS.map((c) => <option key={c}>{c}</option>)}
          </select>
          <select data-testid="gallery-form-room" value={form.room_id} onChange={(e) => setForm({ ...form, room_id: e.target.value })}>
            <option value="">General property gallery</option>
            {rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </div>
        <div className="flex flex-wrap items-center gap-6 mt-3 text-sm">
          <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" style={{ width: "auto" }} data-testid="gallery-form-cover" checked={form.is_cover} onChange={(e) => setForm({ ...form, is_cover: e.target.checked })} /> Cover / main photo</label>
          <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" style={{ width: "auto" }} data-testid="gallery-form-active" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Show on website</label>
        </div>
        <button data-testid="gallery-form-add" onClick={add} disabled={busy} className="btn-primary mt-4">{busy ? "Adding…" : "Add Photo"}</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {images.map((g) => (
          <div key={g.id} className="card" style={{ padding: 10 }} data-testid={`gallery-item-${g.id}`}>
            <img src={fileUrl(g.image_url)} alt={g.alt_text} className="w-full aspect-square object-cover rounded-lg mb-2" />
            <div className="text-xs font-medium truncate">{g.alt_text || g.title || "—"}</div>
            <div className="text-xs mb-2" style={{ color: "var(--muted)" }}>{g.category}{g.is_cover && " · cover"}{rooms.find((r) => r.id === g.room_id) ? ` · ${rooms.find((r) => r.id === g.room_id).name}` : ""}</div>
            <div className="flex flex-wrap gap-1">
              <button data-testid={`gallery-toggle-${g.id}`} onClick={() => update(g, { active: g.active === false })} className="btn-outline" style={{ padding: "4px 8px", fontSize: 11 }}>{g.active !== false ? "Hide" : "Show"}</button>
              <button data-testid={`gallery-cover-${g.id}`} onClick={() => update(g, { is_cover: !g.is_cover })} className="btn-outline" style={{ padding: "4px 8px", fontSize: 11 }}>{g.is_cover ? "Unset cover" : "Set cover"}</button>
              <button data-testid={`gallery-delete-${g.id}`} onClick={() => remove(g)} className="p-1" style={{ color: "var(--terracotta)" }}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
      {images.length === 0 && <p className="text-sm" style={{ color: "var(--muted)" }}>No photos yet.</p>}
    </div>
  );
}
